import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { MagnifyingGlass, Eye } from '@phosphor-icons/react';

export default function AdminTrading() {
  const [accounts, setAccounts] = useState([]);
  const [reports, setReports] = useState({});
  const [audit, setAudit] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    try {
      const [a, r, l] = await Promise.all([
        api.get('/trading/admin/accounts' + (search ? `?search=${encodeURIComponent(search)}` : '')),
        api.get('/trading/admin/reports/overview'),
        api.get('/trading/admin/audit-log?limit=30'),
      ]);
      setAccounts(a.data); setReports(r.data); setAudit(l.data);
    } catch (err) {/* */}
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [search]);

  const loadDetail = async (user_id) => {
    try { const { data } = await api.get(`/trading/admin/accounts/${user_id}`); setDetail(data); setSelected(user_id); }
    catch (err) { toast.error('Failed to load'); }
  };

  const toggleAccount = async (user_id, field, value) => {
    await api.patch(`/trading/admin/accounts/${user_id}`, { [field]: value });
    toast.success('Updated'); load(); if (selected === user_id) loadDetail(user_id);
  };
  const adjust = async (user_id) => {
    const amount = prompt('Amount (positive=deposit, negative=withdrawal):');
    if (!amount) return;
    const reason = prompt('Reason:') || 'Admin adjustment';
    try { await api.post(`/trading/admin/accounts/${user_id}/adjust-balance`, { amount: parseFloat(amount), reason }); toast.success('Balance adjusted'); load(); if (selected === user_id) loadDetail(user_id); }
    catch (err) { toast.error('Failed'); }
  };
  const forceClose = async (pid, user_id) => {
    if (!window.confirm('Force close this position?')) return;
    await api.post(`/trading/admin/positions/${pid}/close`);
    toast.success('Position closed by admin');
    loadDetail(user_id); load();
  };

  return (
    <div className="space-y-4" data-testid="admin-trading-panel">
      <div className="glass p-4 rounded-lg grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {[
          {l:'Accounts', v: reports.total_accounts},
          {l:'Open Pos', v: reports.open_positions, c:'text-blue-400'},
          {l:'Pending', v: reports.pending_orders},
          {l:'Closed', v: reports.closed_trades},
          {l:'Client P/L', v: `$${reports.client_pnl_total ?? 0}`, c: (reports.client_pnl_total ?? 0) >= 0 ? 'text-green-400' : 'text-red-400'},
          {l:'Deposits', v: `$${reports.total_deposits ?? 0}`, c:'text-green-400'},
          {l:'Withdrawals', v: `$${reports.total_withdrawals ?? 0}`, c:'text-red-400'},
        ].map(({l,v,c}) => (
          <div key={l} className="p-3 rounded-md bg-surface-base border border-white/5">
            <div className="data-label">{l}</div>
            <div className={`stat-num text-xl mt-1 ${c || ''}`}>{v ?? 0}</div>
          </div>
        ))}
      </div>

      <div className="glass p-4 rounded-lg">
        <div className="flex items-center gap-2 mb-3">
          <MagnifyingGlass size={16} className="text-muted-foreground" />
          <Input placeholder="Search by name / email / account #" value={search} onChange={e => setSearch(e.target.value)} className="bg-surface-base border-white/10 max-w-md" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-widest text-muted-foreground border-b border-white/5">
              <tr>{['User','Account','Balance','Equity','Margin','Free Margin','Open','Lev','Status','Actions'].map(h => <th key={h} className="p-2 text-left">{h}</th>)}</tr>
            </thead>
            <tbody>
              {accounts.length === 0 && <tr><td colSpan="10" className="p-6 text-center text-muted-foreground">No accounts.</td></tr>}
              {accounts.map(a => (
                <tr key={a.account.id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-2">
                    <div className="font-semibold">{a.user.full_name}</div>
                    <div className="text-muted-foreground">{a.user.email}</div>
                  </td>
                  <td className="p-2 font-mono">{a.account.account_number}</td>
                  <td className="p-2 font-mono">${a.account.balance?.toFixed(2)}</td>
                  <td className="p-2 font-mono text-saffron">${a.account.equity?.toFixed(2)}</td>
                  <td className="p-2 font-mono">${a.account.margin_used?.toFixed(2)}</td>
                  <td className="p-2 font-mono">${a.account.free_margin?.toFixed(2)}</td>
                  <td className="p-2 font-mono">{a.open_positions_count}</td>
                  <td className="p-2 font-mono">1:{a.account.leverage}</td>
                  <td className="p-2">
                    <div className="flex flex-col gap-1">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${a.account.trading_enabled && !a.account.frozen ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'}`}>
                        {a.account.frozen ? 'FROZEN' : a.account.trading_enabled ? 'ENABLED' : 'DISABLED'}
                      </span>
                    </div>
                  </td>
                  <td className="p-2">
                    <div className="flex gap-1 flex-wrap">
                      <button onClick={() => loadDetail(a.user.id)} data-testid={`view-${a.user.id}`} className="text-xs px-2 py-1 rounded bg-saffron/10 text-saffron hover:bg-saffron/20"><Eye size={12} className="inline mr-1" />View</button>
                      <button onClick={() => toggleAccount(a.user.id, 'trading_enabled', !a.account.trading_enabled)} className="text-xs px-2 py-1 rounded bg-white/5 hover:bg-white/10">{a.account.trading_enabled ? 'Disable' : 'Enable'}</button>
                      <button onClick={() => toggleAccount(a.user.id, 'frozen', !a.account.frozen)} className="text-xs px-2 py-1 rounded bg-white/5 hover:bg-white/10">{a.account.frozen ? 'Unfreeze' : 'Freeze'}</button>
                      <button onClick={() => adjust(a.user.id)} className="text-xs px-2 py-1 rounded bg-white/5 hover:bg-white/10">Adjust $</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => { setDetail(null); setSelected(null); }}>
          <div className="glass-strong rounded-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="font-heading text-xl font-bold">{detail.user.full_name}</div>
                <div className="text-xs text-muted-foreground">{detail.user.email} · {detail.account.account_number}</div>
              </div>
              <button onClick={() => { setDetail(null); setSelected(null); }} className="p-1 hover:bg-white/5 rounded">✕</button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="p-3 bg-surface-base rounded"><div className="data-label">Balance</div><div className="font-mono text-lg mt-1">${detail.account.balance?.toFixed(2)}</div></div>
              <div className="p-3 bg-surface-base rounded"><div className="data-label">Equity</div><div className="font-mono text-lg mt-1 text-saffron">${detail.account.equity?.toFixed(2)}</div></div>
              <div className="p-3 bg-surface-base rounded"><div className="data-label">Floating P/L</div><div className={`font-mono text-lg mt-1 ${detail.account.floating_pl >= 0 ? 'text-green-400' : 'text-red-400'}`}>${detail.account.floating_pl?.toFixed(2)}</div></div>
              <div className="p-3 bg-surface-base rounded"><div className="data-label">Margin Level</div><div className="font-mono text-lg mt-1">{detail.account.margin_level}%</div></div>
            </div>
            <h4 className="font-heading font-bold mb-2">Open Positions ({detail.open_positions.length})</h4>
            <div className="overflow-x-auto mb-4"><table className="w-full text-xs">
              <thead><tr className="border-b border-white/5 text-[10px] uppercase text-muted-foreground">
                {['Ticket','Symbol','Dir','Vol','Open','Cur','SL','TP','P/L','Actions'].map(h => <th key={h} className="p-2 text-left">{h}</th>)}
              </tr></thead>
              <tbody>{detail.open_positions.map(p => (
                <tr key={p.id} className="border-b border-white/5">
                  <td className="p-2 font-mono">{p.ticket}</td>
                  <td className="p-2 font-mono">{p.symbol}</td>
                  <td className={`p-2 font-bold ${p.direction === 'BUY' ? 'text-green-400' : 'text-red-400'}`}>{p.direction}</td>
                  <td className="p-2 font-mono">{p.volume}</td>
                  <td className="p-2 font-mono">{p.open_price}</td>
                  <td className="p-2 font-mono">{p.current_price?.toFixed?.(4)}</td>
                  <td className="p-2 font-mono">{p.stop_loss || '—'}</td>
                  <td className="p-2 font-mono">{p.take_profit || '—'}</td>
                  <td className={`p-2 font-mono font-bold ${p.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>${p.profit}</td>
                  <td className="p-2"><button onClick={() => forceClose(p.id, detail.user.id)} className="text-xs px-2 py-1 rounded bg-red-500/20 text-red-400">Force Close</button></td>
                </tr>
              ))}</tbody>
            </table></div>
            <h4 className="font-heading font-bold mb-2">Recent History ({detail.history.length})</h4>
            <div className="overflow-x-auto max-h-64"><table className="w-full text-xs">
              <thead><tr className="border-b border-white/5 text-[10px] uppercase text-muted-foreground">
                {['Time','Type','Symbol','Vol','P/L'].map(h => <th key={h} className="p-2 text-left">{h}</th>)}
              </tr></thead>
              <tbody>{detail.history.slice(0,20).map(h => (
                <tr key={h.id} className="border-b border-white/5">
                  <td className="p-2 text-muted-foreground">{new Date(h.close_time).toLocaleString()}</td>
                  <td className="p-2">{h.type}</td>
                  <td className="p-2 font-mono">{h.symbol}</td>
                  <td className="p-2 font-mono">{h.volume}</td>
                  <td className={`p-2 font-mono font-bold ${h.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>${h.profit}</td>
                </tr>
              ))}</tbody>
            </table></div>
          </div>
        </div>
      )}

      <div className="glass p-4 rounded-lg">
        <h3 className="font-heading font-bold text-lg mb-3">Admin Audit Log (last 30)</h3>
        <div className="space-y-1 max-h-72 overflow-y-auto text-xs">
          {audit.length === 0 && <div className="text-muted-foreground">No audit entries.</div>}
          {audit.map(a => (
            <div key={a.id} className="p-2 bg-surface-base rounded flex items-center justify-between gap-2 flex-wrap">
              <div className="flex-1 min-w-0">
                <span className="font-semibold text-saffron">{a.action}</span>
                <span className="text-muted-foreground ml-2">by {a.actor_email}</span>
                {a.target_user_id && <span className="text-muted-foreground ml-2">→ user {a.target_user_id.slice(0,8)}</span>}
                <span className="ml-2 font-mono text-[10px] text-muted-foreground">{JSON.stringify(a.details).slice(0,120)}</span>
              </div>
              <span className="text-[10px] text-muted-foreground">{new Date(a.timestamp).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
